import React, { useState } from "react";

import "./AmbulatoryClinics.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { motion } from "framer-motion";
import { CDN_BASE_URL } from "../../config/cdn";

const HeroSection = ({ bgImage }) => {
  // Convert: "/assets/images/ambulatory.png" → "/images/ambulatory.png"
  const finalBg = bgImage.replace("/assets", "");

  return (
    <section
      className="hero-section"
      style={{
        background: `url("${CDN_BASE_URL}${finalBg}") center center / cover no-repeat`,
      }}
    >
      <div className="hero-content">
        <h1>
          Ambulatory Services <br /> <span>& Clinics</span>
        </h1>
      </div>
    </section>
  );
};


function IntroSection({ bgColor, pageName }) {
  const [open, setOpen] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    hospital: "",
    facilityType: "",
    query: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch("http://localhost:5000/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pageName: pageName || "Ambulatory Clinics Page",
          ...formData,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setOpen(false);          // close form popup
        setSuccessPopup(true);   // show thank you popup

        setTimeout(() => {
          setSuccessPopup(false);
        }, 3000);
      }
    } catch (err) {
      console.error("Error saving data:", err);
    }
  };

  return (
    <section className="intro-section" style={{ "--bg-color": bgColor }}>
      <div className="intro-left">
        <h2>Optimizing Outpatient Practices with Ambulatory RCM</h2>
      </div>

      <div className="intro-right">
        <p>
          Revolutionize your outpatient operations with our tailored Ambulatory
          Revenue Cycle Management solutions.
        </p>

        <button className="cta-button" onClick={() => setOpen(true)}>
          Let's Talk.
        </button>
      </div>

      {/* ---------- CONTACT FORM POPUP ---------- */}
      {open && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Contact Us</h3>
            <form onSubmit={handleSubmit}>
              <label>Name *</label>
              <input
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
              />

              <label>Email *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
              />

              <label>Phone</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />

              <label>Hospital</label>
              <input
                name="hospital"
                value={formData.hospital}
                onChange={handleChange}
              />

              <label>Healthcare Facility Type *</label>
              <select
                name="facilityType"
                required
                value={formData.facilityType}
                onChange={handleChange}
              >
                <option value="">Select a facility type</option>
                <option>Clinic</option>
                <option>Hospital</option>
                <option>Diagnostic Center</option>
                <option>Specialty Center</option>
              </select>

              <label>Query</label>
              <textarea
                name="query"
                value={formData.query}
                onChange={handleChange}
              ></textarea>

              <button type="submit" className="submit-btn">
                Schedule my demo
              </button>
            </form>

            <button className="close-btn" onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ---------- SUCCESS POPUP ---------- */}
      {successPopup && (
        <div className="modal-overlay">
          <div className="modal-box success-modal">
            <h3>Thank You!</h3>
            <p>Our specialist will contact you soon.</p>
          </div>
        </div>
      )}
    </section>
  );
}

const UnderstandingSection = ({ bgColor, image }) => {
  // Convert: "/assets/images/Ambulatory22.png" → "/images/Ambulatory22.png"
  const finalImage = image.replace("/assets", "");

  return (
    <section
      className="understanding-section"
      style={{
        backgroundColor: bgColor,
        backgroundImage: `url("${CDN_BASE_URL}${finalImage}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      <div className="understanding-content">
        <h2>Understanding Ambulatory RCM</h2>
        <p>
          Ambulatory Revenue Cycle Management (RCM) governs the financial workflows
          of outpatient healthcare, managing everything from patient intake to final
          payment. It encompasses critical functions such as billing, coding, and
          collections, ensuring that services are reimbursed promptly and efficiently.
        </p>
      </div>
    </section>
  );
};


const BenefitsSection = ({ bgColor }) => {
  return (
    <section className="benefits-section" style={{ backgroundColor: bgColor }}>
      <div className="benefit">
        <img
          src={`${CDN_BASE_URL}/icons/higher-first-pass-clearances-streamlined-coding-an.jpg`}
          alt="Higher First Pass Clearances"
          className="benefit-icon"
        />
        <h4>Higher First-Pass Clearances</h4>
        <p>
          Streamlined coding and billing workflows ensure your claims are approved
          on the first try, accelerating reimbursements.
        </p>
      </div>

      <div className="benefit">
        <img
          src={`${CDN_BASE_URL}/icons/enhanced-cash-flow---icon.jpg`}
          alt="Enhanced Cash Flow"
          className="benefit-icon"
        />
        <h4>Enhanced Cash Flow</h4>
        <p>
          Optimized workflows accelerate payment processing and minimize reimbursement
          delays, strengthening financial stability.
        </p>
      </div>

      <div className="benefit">
        <img
          src={`${CDN_BASE_URL}/icons/lower-overhead-costs-efficient-rcm-processes-reduc.jpg`}
          alt="Lower Overhead Costs"
          className="benefit-icon"
        />
        <h4>Lower Overhead Costs</h4>
        <p>
          Efficient RCM processes cut down on administrative expenses by lessening
          the in-house resource burden for billing, coding, and collections.
        </p>
      </div>

      <div className="benefit">
        <img
          src={`${CDN_BASE_URL}/icons/data-driven-insights-leverage-predictive-analytics.jpg`}
          alt="Data Driven Insights"
          className="benefit-icon"
        />
        <h4>Data-Driven Insights</h4>
        <p>
          Leverage predictive analytics to monitor reimbursement trends, identify
          underperforming claims, and forecast revenue.
        </p>
      </div>
    </section>
  );
};

const ChallengesSection = ({ bgColor, image }) => {
  // Convert local path: "/assets/images/Ambulatory223.jpg" → "/images/Ambulatory223.jpg"
  const finalImage = image.replace("/assets", "");

  return (
    <section className="challenges-section" style={{ backgroundColor: bgColor }}>
      <h2>RCM Challenges in Outpatient Settings</h2>
      <div className="challenges-content">
        <img
          src={`${CDN_BASE_URL}${finalImage}`}
          alt="Challenges"
        />
        <ul>
          <li>Slow Payments and Extended AR Periods</li>
          <li>Inefficient Data Handling and Integration with EHR Data</li>
          <li>Frequent Coding Errors and Higher Claim Denial Volumes</li>
          <li>Escalating Administrative Workloads</li>
          <li>Overlooked Underpayments and Reimbursement Discrepancies</li>
        </ul>
      </div>
    </section>
  );
};


/* ✅ FULL TESTIMONIAL DATA RESTORED */
const testimonials = [
  {
    quote:
      "Partnering with MedRCM transformed our billing process. They quickly identified coding gaps, streamlined claims for both Vision and medical services, and ensured accurate payor routing using a modifier 25, 59, RT, LT, E2, E4. Since then, our clean claim rate has risen above 98%, and reimbursement times have improved dramatically. Their deep knowledge of Ophthalmology billing and up-to-date guidance on payor requirements is unmatched.",
    author:
      "Head of Ophthalmology for a large Hospital based out of Bronx, NY",
  },
  {
    quote:
      "MedRCM has profoundly elevated our revenue cycle efficiency. Their nuanced understanding of E/M coding, Medicare guidelines, and value-based care billing has significantly improved our clean claim rate and reduced AR days. Their proactive audits, real-time feedback, and front office training have streamlined both our operations and patient collections.",
    author:
      "Billing Manager of a large Internal Medicine Group Practice based out of NY",
  },
  {
    quote:
      "As a cardiovascular facility, we sincerely appreciate the professionalism and efficiency with which the MedRCM team addresses our patients' billing concerns. Many of our regular patients have expressed gratitude for the prompt and clear resolutions provided.Your dedicated support in AR, billing, and coding has significantly streamlined our operations and enhanced patient satisfaction. Thank you!” ",
    author:
      "Office Manager of a large Cardiovascular Practice based out of IN",
  },
  {
    quote:
      "MedRCM has made a measurable difference in the way we manage billing. From their proactive communication to strategic problem-solving, they’ve helped us avoid late filings, reduce our outstanding A/R, and adapt smoothly to growing operational demands — including the addition of new Providers. What stands out most is their ability to anticipate issues and implement practical solutions before they affect our bottom line.",
    author: "Office Manager at a Sleep Management Practice based out of CA",
  },
  {
    quote:
      "MedRCM brought structure, speed, and accuracy to our prior authorization process. Their team worked closely with us to build a workflow that fits our clinic’s needs, resulting in faster turnaround times and fewer issues.. We also now benefit from clear, reliable financial reports that support smarter decision-making. MedRCM continues to deliver both operational improvements and real financial impact.”",
    author:
      "Office Manager at a large Pain Management Group Practice based out of OH",
  },
];

const TestimonialSection = ({ bgColor }) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
  };

  return (
    <section
      className="testimonial-section"
      style={{ backgroundColor: bgColor || "#f8f9fa", padding: "60px 20px" }}
    >
      <h2 className="text-center text-2xl font-semibold mb-10">
        What Hospitals Say About Us
      </h2>
      <Slider {...settings}>
        {testimonials.map((t, index) => (
          <div key={index} className="testimonial-card">
            <blockquote>“{t.quote}”</blockquote>
            <p className="testimonial-author">
              <strong>{t.author}</strong>
            </p>
          </div>
        ))}
      </Slider>
    </section>
  );
};

const DemoSection = ({ bgColor }) => (
  <section
    className="demo-section"
    style={{
      backgroundColor: bgColor || "#f5fafa",
      padding: "80px 60px",
    }}
  >
    <div className="demo-container">
      <motion.div
        className="demo-left"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h2>Set up a demo today.</h2>
        <p>
          Discover how <strong>MedRCM</strong> can transform your billing
          operations, improve clean claim rates, and enhance revenue recovery.
          Schedule a personalized demo and experience our intelligent RCM
          platform in action.
        </p>
      </motion.div>

      <motion.form
        className="demo-form"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <input type="text" placeholder="Name *" />
        <input type="email" placeholder="Email *" />
        <input type="text" placeholder="Phone" />
        <input type="text" placeholder="Hospital" />
        <input type="text" placeholder="Healthcare Facility Type *" />
        <textarea placeholder="Add any relevant information below"></textarea>
        <button type="submit">Schedule my demo</button>
      </motion.form>
    </div>
  </section>
);

const AmbulatoryClinics = () => (
  <>
    <HeroSection bgImage="/assets/images/ambulatory.png" />
    <IntroSection bgColor="#ffffff" pageName="Ambulatory Clinics Page" />
    <UnderstandingSection bgColor="#ffffff" image="/assets/images/Ambulatory22.png" />
    <BenefitsSection bgColor="#0E4C47" />
    <ChallengesSection bgColor="#ffffff" image="/assets/images/Ambulatory223.jpg" />
    <TestimonialSection bgColor="#E8F3F9" />
    <DemoSection bgColor="#ffffff" />
  </>
);

export default AmbulatoryClinics;
